"use client";

import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Button from "./Button";

type MobilePlatform = "ios" | "android" | "other";

// const WHATSAPP_BROWSER_REGEX = /(WhatsApp|WA4A|WA4I)/i;
const IOS_REGEX = /iPhone|iPad|iPod/i;
const ANDROID_REGEX = /Android/i;

function getPlatform(userAgent: string): MobilePlatform {
  if (IOS_REGEX.test(userAgent)) {
    return "ios";
  }

  if (ANDROID_REGEX.test(userAgent)) {
    return "android";
  }

  return "other";
}

function copyWithFallback(text: string) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

function InstructionCard({ title, steps }: { title: string; steps: string[] }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left">
      <p className="text-sm font-semibold text-black">{title}</p>
      <ol className="mt-3 space-y-2 text-sm text-slate-700">
        {steps.map((step, index) => (
          <li key={step} className="flex items-start gap-3">
            {/* <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-black border">
              {index + 1}
            </span> */}
            <span>{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function WhatsAppBrowserBlocker({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [platform, setPlatform] = useState<MobilePlatform>("other");
  const [hasCopied, setHasCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    const userAgent = window.navigator.userAgent;

    setPlatform(getPlatform(userAgent));
    setHasCopied(false);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [open]);

  if (!open) {
    return null;
  }

  const isIos = platform === "ios";
  const isAndroid = platform === "android";

  const handleCopyLink = async () => {
    const currentUrl = window.location.href;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(currentUrl);
      } else {
        copyWithFallback(currentUrl);
      }

      setHasCopied(true);
      toast.success("Link copied. Paste it into Safari or Chrome.");
    } catch {
      toast.error("Could not copy the link from WhatsApp.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-1000 flex items-center justify-center bg-slate-950/72 p-4 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        aria-modal="true"
        className="relative w-full max-w-lg overflow-hidden rounded-[28px] border border-white/60 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.35)]"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        {/* <div className="absolute inset-x-0 top-0 h-1.5 bg-linear-to-r from-emerald-500 via-cyan-500 to-blue-500" /> */}
        <div className="p-6 sm:p-8">
          <button
            aria-label="Close"
            className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
            onClick={onClose}
            type="button"
          >
            x
          </button>
          <div className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
            Open in Browser
          </div>

          <div className="mt-5 space-y-3 text-left">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-950 sm:text-[2rem]">
              Downloads are not supported inside WhatsApp.
            </h2>
            <p className="text-sm leading-6 text-slate-600 sm:text-base">
              Please open this page in Safari or Chrome to download passes,
              images, or other files from the website.
            </p>
          </div>

          <div className="mt-6 grid gap-3">
            {isIos && (
              <InstructionCard
                title="On iPhone"
                steps={["Tap the share icon", 'Tap "Open in Safari"']}
              />
            )}

            {isAndroid && (
              <InstructionCard
                title="On Android"
                steps={[
                  "Tap the 3-dot menu at the top right",
                  'Tap "Open in browser"',
                ]}
              />
            )}

            {!isIos && !isAndroid && (
              <>
                <InstructionCard
                  title="On iPhone"
                  steps={["Tap the share icon", 'Tap "Open in Safari"']}
                />
                <InstructionCard
                  title="On Android"
                  steps={[
                    "Tap the 3-dot menu at the top right",
                    'Tap "Open in browser"',
                  ]}
                />
              </>
            )}
          </div>

          <div className="mt-6 rounded-2xl border border-gray-300 px-4 py-3 text-left text-xs text-slate-700">
            If you cannot find the WhatsApp browser menu, copy this page link
            and paste it into Safari or Chrome.
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              design="secondary"
              label={hasCopied ? "Link copied" : "Copy link"}
              onClick={handleCopyLink}
              type="button"
            >
              {hasCopied ? "Link copied" : "Copy link"}
            </Button>
            <p className="text-xs leading-5 text-slate-500 sm:text-sm">
              After copying, switch to Safari or Chrome and paste the link into
              the address bar.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
